import { useState, useEffect, useMemo, type ReactNode, type KeyboardEvent } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx } from "./Primitives";
import type { Market, Principal } from "../types";
import ApiKeys from "./ApiKeys";
import Agents from "./Agents";
import InfoAdmin from "./InfoAdmin";
import type { Partner } from "../types";

type Tab = "users" | "principals" | "markets" | "content" | "api" | "bots" | "info";

const TABS: { id: Tab; label: string; sub: string }[] = [
  { id: "users", label: "Users", sub: "Who can sign in to the CRM" },
  { id: "principals", label: "Principals", sub: "The companies Saga represents" },
  { id: "markets", label: "Markets", sub: "Countries we cover — follows info.saga-advisory.com" },
  { id: "content", label: "Content", sub: "Edit or delete deals, contacts, tasks, minutes and organisations" },
  { id: "api", label: "API keys", sub: "Keys the websites use to send leads into the CRM" },
  { id: "bots", label: "Bots & agents", sub: "AI agents with their own access — e.g. your personal Grok agent" },
  { id: "info", label: "Info site", sub: "Administer info.saga-advisory.com — partners, sources, review queue, the daily briefing" },
];

export default function Settings({ myId, onChanged, partnersMap, initialTab }: {
  myId: string; onChanged: () => void; partnersMap: Record<string, Partner>; initialTab?: string | null;
}) {
  // Deep links: ?screen=settings&id=markets opens that tab.
  const [tab, setTab] = useState<Tab>(() => (TABS.some(t => t.id === initialTab) ? initialTab as Tab : "users"));
  return (
    <div className="screen settings-screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Settings</h1>
          <div className="page-sub">{TABS.find(t => t.id === tab)!.sub}</div>
        </div>
        <div className="seg">
          {TABS.map(t => (
            <button key={t.id} className={clsx("seg-btn", tab === t.id && "active")} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
      </div>
      {tab === "users" && <UsersTab myId={myId} onChanged={onChanged} />}
      {tab === "principals" && <PrincipalsTab onChanged={onChanged} />}
      {tab === "markets" && <MarketsTab />}
      {tab === "info" && <InfoAdmin />}
      {tab === "content" && <ContentTab onChanged={onChanged} />}
      {tab === "api" && <ApiKeys />}
      {tab === "bots" && <Agents partners={partnersMap} me={myId} />}
    </div>
  );
}

// ─── Shared bits ───

function useNotice() {
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  useEffect(() => {
    if (notice?.kind !== "ok") return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);
  const el = notice ? <div className={clsx("set-notice", notice.kind)} role="status">{notice.text}</div> : null;
  return {
    el,
    ok: (text: string) => setNotice({ kind: "ok", text }),
    err: (e: unknown) => setNotice({ kind: "err", text: e instanceof Error ? e.message : String(e) }),
  };
}

function Field({ label, children, wide, hint }: { label: string; children: ReactNode; wide?: boolean; hint?: string }) {
  return (
    <label className={clsx("set-field", wide && "wide")}>
      <span className="set-label">{label}</span>
      {children}
      {hint && <span className="set-hint">{hint}</span>}
    </label>
  );
}

function ChipInput({ values, onChange, placeholder }: { values: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState("");
  const add = (raw: string) => {
    const parts = raw.split(",").map(s => s.trim()).filter(Boolean);
    const next = [...values];
    for (const p of parts) if (!next.some(v => v.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next);
    setDraft("");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && draft.trim()) { e.preventDefault(); add(draft); }
    if (e.key === "Backspace" && !draft && values.length) onChange(values.slice(0, -1));
  };
  return (
    <div className="set-chips">
      {values.map(v => (
        <span key={v} className="set-chip">
          {v}
          <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter(x => x !== v))}>×</button>
        </span>
      ))}
      <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={onKey}
        onBlur={() => draft.trim() && add(draft)} placeholder={values.length ? "" : placeholder} />
    </div>
  );
}

function ToggleChips({ options, selected, onChange }: { options: string[]; selected: string[]; onChange: (v: string[]) => void }) {
  const all = [...options, ...selected.filter(s => !options.includes(s))];
  return (
    <div className="set-toggles">
      {all.map(o => {
        const on = selected.includes(o);
        return (
          <button type="button" key={o} className={clsx("set-toggle", on && "on")}
            onClick={() => onChange(on ? selected.filter(s => s !== o) : [...selected, o])}>
            {on && I.check({ size: 12, w: 2.2 })}{o}
          </button>
        );
      })}
      {all.length === 0 && <span className="set-hint">No markets yet — add them under Markets.</span>}
    </div>
  );
}

function RowActions({ onEdit, onDelete, deleteLabel = "Delete" }: { onEdit?: () => void; onDelete?: () => void; deleteLabel?: string }) {
  return (
    <div className="set-row-actions">
      {onEdit && <button className="btn ghost sm" onClick={onEdit}>Edit</button>}
      {onDelete && <button className="btn ghost sm set-danger" onClick={onDelete}>{I.trash({ size: 13, w: 1.8 })}{deleteLabel}</button>}
    </div>
  );
}

// ─── Users ───

interface AdminUser {
  id: string; name: string; initials: string; role: string; base: string; email: string;
  photo: string | null; is_admin: boolean; active: boolean; gmail_connected?: boolean;
}

const EMPTY_USER = { name: "", email: "", role: "", base: "", initials: "", is_admin: false, active: true };

function UsersTab({ myId, onChanged }: { myId: string; onChanged: () => void }) {
  const { data: users, refetch } = useData<AdminUser[]>("/api/admin/users");
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<typeof EMPTY_USER>(EMPTY_USER);
  const [busy, setBusy] = useState(false);
  const n = useNotice();

  const open = (u?: AdminUser) => {
    setEditing(u ? u.id : "new");
    setForm(u ? { name: u.name, email: u.email, role: u.role, base: u.base, initials: u.initials, is_admin: u.is_admin, active: u.active } : EMPTY_USER);
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") await api.post("/api/admin/users", form);
      else await api.put(`/api/admin/users/${editing}`, form);
      n.ok(editing === "new" ? `${form.name} added — they can now sign in with Google as ${form.email}` : "Saved");
      setEditing(null); refetch(); onChanged();
    } catch (e) { n.err(e); } finally { setBusy(false); }
  };
  const remove = async (u: AdminUser) => {
    if (!confirm(`Remove ${u.name}? They will no longer be able to sign in.`)) return;
    try {
      const r = await api.delete<{ deleted?: boolean; deactivated?: boolean }>(`/api/admin/users/${u.id}`);
      n.ok(r.deactivated ? `${u.name} owns deals, tasks or emails, so they were deactivated (history kept). Reactivate any time.` : `${u.name} removed`);
      refetch(); onChanged();
    } catch (e) { n.err(e); }
  };

  const formEl = (
    <div className="set-form">
      <div className="set-grid">
        <Field label="Full name"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus /></Field>
        <Field label="Google email" hint="They sign in with Google using exactly this address">
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Role"><input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g. Partner — Technical" /></Field>
        <Field label="Base"><input value={form.base} onChange={e => setForm({ ...form, base: e.target.value })} placeholder="e.g. Stavanger" /></Field>
        <Field label="Initials"><input value={form.initials} maxLength={3} onChange={e => setForm({ ...form, initials: e.target.value })} placeholder="auto" /></Field>
        <div className="set-checks">
          <label className="set-check"><input type="checkbox" checked={form.is_admin} disabled={editing === myId}
            onChange={e => setForm({ ...form, is_admin: e.target.checked })} /> Admin (can open Settings)</label>
          {editing !== "new" && (
            <label className="set-check"><input type="checkbox" checked={form.active} disabled={editing === myId}
              onChange={e => setForm({ ...form, active: e.target.checked })} /> Active (can sign in)</label>
          )}
        </div>
      </div>
      <div className="set-form-foot">
        <button className="btn ghost sm" onClick={() => setEditing(null)}>Cancel</button>
        <button className="btn primary sm" disabled={busy || !form.name.trim() || !form.email.trim()} onClick={save}>
          {editing === "new" ? "Add user" : "Save"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="card set-card">
      <div className="set-card-head">
        <div className="section-label">{users ? `${users.filter(u => u.active).length} active users` : "Users"}</div>
        <button className="btn primary sm" onClick={() => open()}>{I.plus({ size: 13, w: 2 })}Add user</button>
      </div>
      {n.el}
      {editing === "new" && formEl}
      <div className="set-rows">
        {(users || []).map(u => (
          <div key={u.id}>
            <div className={clsx("set-row", !u.active && "muted")}>
              <span className="set-avatar">{u.initials}</span>
              <div className="set-row-main">
                <div className="set-row-title">
                  {u.name}
                  {u.is_admin && <span className="set-badge gold">Admin</span>}
                  {!u.active && <span className="set-badge">Deactivated</span>}
                  {u.id === myId && <span className="set-badge">You</span>}
                </div>
                <div className="set-row-sub">{[u.email, u.role, u.base].filter(Boolean).join(" · ")}{u.gmail_connected ? " · Gmail connected" : ""}</div>
              </div>
              <RowActions onEdit={() => open(u)} onDelete={u.id === myId || !u.active ? undefined : () => remove(u)} deleteLabel="Remove" />
            </div>
            {editing === u.id && formEl}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Principals ───

interface AdminPrincipal extends Principal { deal_count: number }
interface Draft {
  name: string; website: string; what: string; sector: string; base: string; color: string;
  keywords: string[]; markets: string[];
}
const EMPTY_DRAFT: Draft = { name: "", website: "", what: "", sector: "", base: "", color: "#2D6FA3", keywords: [], markets: [] };

function PrincipalsTab({ onChanged }: { onChanged: () => void }) {
  const { data: principals, refetch } = useData<AdminPrincipal[]>("/api/admin/principals");
  const { data: markets } = useData<Market[]>("/api/markets");
  const [url, setUrl] = useState("");
  const [reading, setReading] = useState(false);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [source, setSource] = useState<"ai" | "page" | null>(null);
  const [existing, setExisting] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const n = useNotice();
  const marketNames = (markets || []).map(m => m.name);

  const suggest = async () => {
    setReading(true);
    try {
      const { existing: dup, source: src, ...s } = await api.post<Draft & { source: "ai" | "page"; existing: { id: string; name: string } | null }>(
        "/api/admin/principals/suggest", { url });
      setDraft({ ...EMPTY_DRAFT, ...s });
      setSource(src);
      setExisting(dup);
      setEditing("new");
    } catch (e) { n.err(e); } finally { setReading(false); }
  };
  const open = (p?: AdminPrincipal) => {
    setSource(null);
    setExisting(null);
    setEditing(p ? p.id : "new");
    setDraft(p ? {
      name: p.name, website: p.website || "", what: p.what || "", sector: p.sector || "", base: p.base || "",
      color: p.color || "#2D6FA3", keywords: p.keywords || [], markets: p.markets || [],
    } : EMPTY_DRAFT);
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") await api.post("/api/admin/principals", draft);
      else await api.put(`/api/admin/principals/${editing}`, draft);
      n.ok(editing === "new" ? `${draft.name} added` : "Saved");
      setEditing(null); setUrl(""); refetch(); onChanged();
    } catch (e) { n.err(e); } finally { setBusy(false); }
  };
  const remove = async (p: AdminPrincipal) => {
    const extra = p.deal_count ? `\n\n${p.deal_count} deal(s) will be kept but no longer linked to ${p.name}.` : "";
    if (!confirm(`Delete ${p.name}?${extra}`)) return;
    try {
      await api.delete(`/api/admin/principals/${p.id}`);
      n.ok(`${p.name} deleted`); refetch(); onChanged();
    } catch (e) { n.err(e); }
  };

  const formEl = (
    <div className="set-form">
      {source && (
        <div className="set-source">
          {I.ai({ size: 14 })}
          {source === "ai"
            ? "Drafted by Saga AI from their website — check and edit before saving."
            : "Filled from the website's own description (AI not configured) — add sector, keywords and markets."}
        </div>
      )}
      {existing && editing === "new" && (
        <div className="set-notice err set-dup">
          {existing.name} is already a principal.
          <button className="btn ghost sm" onClick={() => {
            const p = principals?.find(x => x.id === existing.id);
            if (p) open(p);
          }}>Edit {existing.name} instead</button>
        </div>
      )}
      <div className="set-grid">
        <Field label="Company name"><input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></Field>
        <Field label="Website"><input value={draft.website} onChange={e => setDraft({ ...draft, website: e.target.value })} placeholder="https://…" /></Field>
        <Field label="Sector"><input value={draft.sector} onChange={e => setDraft({ ...draft, sector: e.target.value })} placeholder="e.g. Upstream — wells" /></Field>
        <Field label="Headquarters"><input value={draft.base} onChange={e => setDraft({ ...draft, base: e.target.value })} placeholder="City, Country" /></Field>
        <Field label="Description" wide>
          <textarea rows={3} value={draft.what} onChange={e => setDraft({ ...draft, what: e.target.value })}
            placeholder="What they offer and why it matters to operators" />
        </Field>
        <Field label="Keywords" wide hint="Type and press Enter. Used to match emails and intelligence to this principal.">
          <ChipInput values={draft.keywords} onChange={keywords => setDraft({ ...draft, keywords })} placeholder="e.g. well stimulation, ROV, sand monitoring" />
        </Field>
        <Field label="Markets we work with them in" wide>
          <ToggleChips options={marketNames} selected={draft.markets} onChange={m => setDraft({ ...draft, markets: m })} />
        </Field>
        <Field label="Colour">
          <span className="set-color"><input type="color" value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} />{draft.color}</span>
        </Field>
      </div>
      <div className="set-form-foot">
        <button className="btn ghost sm" onClick={() => setEditing(null)}>Cancel</button>
        <button className="btn primary sm" disabled={busy || !draft.name.trim()} onClick={save}>{editing === "new" ? "Add principal" : "Save"}</button>
      </div>
    </div>
  );

  return (
    <>
      <div className="card set-card">
        <div className="set-card-head">
          <div className="section-label">Add a principal from their website</div>
          <button className="btn ghost sm" onClick={() => open()}>Add manually</button>
        </div>
        <div className="set-url-row">
          <input className="set-url" value={url} onChange={e => setUrl(e.target.value)} placeholder="e.g. fishbones.as"
            onKeyDown={e => e.key === "Enter" && url.trim() && !reading && suggest()} />
          <button className="btn primary" disabled={!url.trim() || reading} onClick={suggest}>
            {I.ai({ size: 14 })}{reading ? "Reading website…" : "Suggest profile"}
          </button>
        </div>
        {n.el}
        {editing === "new" && formEl}
      </div>

      <div className="card set-card">
        <div className="set-card-head"><div className="section-label">{principals?.length ?? 0} principals</div></div>
        <div className="set-rows">
          {(principals || []).map(p => (
            <div key={p.id}>
              <div className="set-row">
                <span className="set-dot" style={{ background: p.color }} />
                <div className="set-row-main">
                  <div className="set-row-title">
                    {p.name}
                    {p.website && <a className="set-link" href={p.website} target="_blank" rel="noreferrer">{p.website.replace(/^https?:\/\/(www\.)?/, "")}</a>}
                  </div>
                  <div className="set-row-sub">{[p.sector, p.base, p.deal_count ? `${p.deal_count} active deal${p.deal_count > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ")}</div>
                  {p.what && <div className="set-row-desc">{p.what}</div>}
                  {((p.keywords?.length ?? 0) > 0 || (p.markets?.length ?? 0) > 0) && (
                    <div className="set-tagline">
                      {(p.markets || []).map(m => <span key={m} className="set-tag market">{m}</span>)}
                      {(p.keywords || []).map(k => <span key={k} className="set-tag">{k}</span>)}
                    </div>
                  )}
                </div>
                <RowActions onEdit={() => open(p)} onDelete={() => remove(p)} />
              </div>
              {editing === p.id && formEl}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Markets ───

interface MarketForm { name: string; region: string; flag: string; tagline: string; search_terms: string[]; publish: boolean }
const EMPTY_MARKET: MarketForm = { name: "", region: "", flag: "", tagline: "", search_terms: [], publish: false };

interface AdminMarket {
  slug: string; name: string; region: string | null; flag: string | null; tagline: string | null; source: "info" | "manual";
  on_info: boolean; hidden: boolean; live: boolean; synced_at: string | null; deal_count: number;
}

function MarketsTab() {
  const { data: markets, refetch } = useData<AdminMarket[]>("/api/admin/markets");
  const { data: infoStatus } = useData<{ writable: boolean }>("/api/admin/markets/info-status");
  const writable = !!infoStatus?.writable;
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<MarketForm>(EMPTY_MARKET);
  const n = useNotice();

  const lastSync = useMemo(() => {
    const t = (markets || []).map(m => m.synced_at).filter(Boolean).sort().pop();
    return t ? new Date(t).toLocaleString() : null;
  }, [markets]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ added: number; updated: number; removed: number; total: number }>("/api/admin/markets/sync", {});
      n.ok(`Synced ${r.total} countries from info.saga-advisory.com — ${r.added} new, ${r.removed} no longer listed`);
      refetch();
    } catch (e) { n.err(e); } finally { setSyncing(false); }
  };
  const save = async () => {
    try {
      let warning: string | null = null;
      if (editing) await api.put(`/api/admin/markets/${editing}`, form);
      else warning = (await api.post<{ warning?: string | null }>("/api/admin/markets", form)).warning || null;
      if (warning) n.err(warning);
      else n.ok(editing ? "Saved — deals and contacts in this market were updated too"
        : form.publish ? `${form.name} added and published on info.saga-advisory.com` : `${form.name} added`);
      setEditing(null); setAdding(false); refetch();
    } catch (e) { n.err(e); }
  };
  const remove = async (m: AdminMarket) => {
    const msg = m.source === "info"
      ? `Hide ${m.name}? It comes from info.saga-advisory.com, so it will stay hidden until you show it again.`
      : `Delete ${m.name}?`;
    if (!confirm(msg + (m.deal_count ? `\n\n${m.deal_count} deal(s) keep "${m.name}" as their market.` : ""))) return;
    try { await api.delete(`/api/admin/markets/${m.slug}`); refetch(); } catch (e) { n.err(e); }
  };
  const deleteFromInfo = async (m: AdminMarket) => {
    if (!confirm(`Permanently delete ${m.name} from info.saga-advisory.com?\n\nThe public country page and its profile are removed for everyone. This can't be undone from the CRM.`)) return;
    try {
      await api.delete(`/api/admin/markets/${m.slug}?from=info`);
      n.ok(`${m.name} deleted from info.saga-advisory.com`); setEditing(null); refetch();
    } catch (e) { n.err(e); }
  };
  const publish = async (m: AdminMarket) => {
    try { await api.post(`/api/admin/markets/${m.slug}/publish`, {}); n.ok(`${m.name} published on info.saga-advisory.com`); refetch(); }
    catch (e) { n.err(e); }
  };
  const unhide = async (m: AdminMarket) => {
    try { await api.put(`/api/admin/markets/${m.slug}`, { hidden: false }); refetch(); } catch (e) { n.err(e); }
  };

  const editingMarket = (markets || []).find(m => m.slug === editing) || null;
  const onInfo = editingMarket ? editingMarket.source === "info" && editingMarket.on_info : form.publish;
  const formEl = (
    <div className="set-form">
      <div className="set-grid">
        <Field label="Country / market"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus /></Field>
        <Field label="Region"><input value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="e.g. West Africa" /></Field>
        <Field label="Flag"><input value={form.flag} onChange={e => setForm({ ...form, flag: e.target.value })} placeholder="auto" /></Field>
        {(onInfo || !editing) && (
          <>
            <Field label="Tagline" wide hint={onInfo ? "Shown on the country page on info.saga-advisory.com" : undefined}>
              <input value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })}
                placeholder="One line on why Saga works in this market" />
            </Field>
            {(onInfo || form.publish) && (
              <Field label="Search terms" wide
                hint={editing ? "These are the terms info uses now — edit them here; saving replaces them on info." : "Names, companies and projects info uses to find articles for this market."}>
                <ChipInput values={form.search_terms} onChange={t => setForm({ ...form, search_terms: t })} placeholder="e.g. ONHYM, Nador West Med" />
              </Field>
            )}
          </>
        )}
        {!editing && (
          <div className="set-checks wide-check">
            <label className="set-check">
              <input type="checkbox" checked={form.publish} disabled={!writable}
                onChange={e => setForm({ ...form, publish: e.target.checked })} />
              Also publish on info.saga-advisory.com
            </label>
            {!writable && <span className="set-hint">Needs SAGA_WRITE_KEY on the CRM server — until then the market is added in the CRM only.</span>}
          </div>
        )}
      </div>
      <div className="set-form-foot">
        {editingMarket && editingMarket.source === "info" && writable && (
          <button className="btn ghost sm set-danger" style={{ marginRight: "auto" }} onClick={() => deleteFromInfo(editingMarket)}>
            Delete from info.saga-advisory.com…
          </button>
        )}
        <button className="btn ghost sm" onClick={() => { setEditing(null); setAdding(false); }}>Cancel</button>
        <button className="btn primary sm" disabled={!form.name.trim()} onClick={save}>
          {editing ? (onInfo && writable ? "Save here and on info" : "Save") : form.publish ? "Add and publish" : "Add market"}
        </button>
      </div>
    </div>
  );

  const groups = useMemo(() => {
    const g = new Map<string, AdminMarket[]>();
    for (const m of markets || []) {
      const key = m.region || "Other";
      g.set(key, [...(g.get(key) || []), m]);
    }
    return [...g.entries()];
  }, [markets]);

  return (
    <div className="card set-card">
      <div className="set-card-head">
        <div>
          <div className="section-label">{(markets || []).filter(m => m.live).length} markets covered</div>
          <div className="set-hint">
            Countries on info.saga-advisory.com are added automatically (at start-up and before each nightly intel pull).
            {lastSync && ` Last sync ${lastSync}.`}
          </div>
        </div>
        <div className="set-row-actions">
          <button className="btn ghost sm" disabled={syncing} onClick={sync}>{syncing ? "Syncing…" : "Sync now"}</button>
          <button className="btn primary sm" onClick={() => { setAdding(true); setEditing(null); setForm({ ...EMPTY_MARKET, publish: writable }); }}>
            {I.plus({ size: 13, w: 2 })}Add market
          </button>
        </div>
      </div>
      {n.el}
      {adding && formEl}
      {groups.map(([region, ms]) => (
        <div key={region} className="set-group">
          <div className="set-group-label">{region}</div>
          {ms.map(m => (
            <div key={m.slug}>
              <div className={clsx("set-row", !m.live && "muted")}>
                <span className="set-flag">{m.flag || "🏳️"}</span>
                <div className="set-row-main">
                  <div className="set-row-title">
                    {m.name}
                    {m.source === "info" && m.on_info && <span className="set-badge info">info.saga-advisory.com</span>}
                    {m.source === "info" && !m.on_info && <span className="set-badge">No longer on info</span>}
                    {m.source === "manual" && <span className="set-badge">Added by hand</span>}
                    {m.hidden && <span className="set-badge">Hidden</span>}
                  </div>
                  {m.deal_count > 0 && <div className="set-row-sub">{m.deal_count} active deal{m.deal_count > 1 ? "s" : ""}</div>}
                </div>
                {m.source === "manual" && !m.hidden && writable && (
                  <button className="btn ghost sm" onClick={() => publish(m)}>Publish to info</button>
                )}
                {m.hidden
                  ? <div className="set-row-actions"><button className="btn ghost sm" onClick={() => unhide(m)}>Show again</button></div>
                  : <RowActions
                      onEdit={() => {
                        setEditing(m.slug); setAdding(false);
                        setForm({ ...EMPTY_MARKET, name: m.name, region: m.region || "", flag: m.flag || "", tagline: m.tagline || "" });
                        // Pre-fill the search terms info.saga-advisory.com uses for this country.
                        if (m.source === "info" && m.on_info) {
                          api.get<{ profile?: { search_terms?: string[] }; search_terms?: string[] }>(`/api/admin/info/country/${encodeURIComponent(m.slug)}`)
                            .then(r => { const t = r.profile?.search_terms ?? r.search_terms; if (Array.isArray(t)) setForm(f => ({ ...f, search_terms: t })); })
                            .catch(() => {});
                        }
                      }}
                      onDelete={() => remove(m)} deleteLabel={m.source === "info" ? "Hide" : "Delete"} />}
              </div>
              {editing === m.slug && formEl}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Content ───

type FieldType = "text" | "textarea" | "number" | "date" | "bool" | "select" | "partner" | "market" | "principal";
interface CField { key: string; label: string; type: FieldType; options?: string[]; required?: boolean }
interface CType { key: string; label: string; archive: boolean; titleField: string; fields: CField[] }
type Row = Record<string, unknown> & { id: string; _subtitle?: string };

function ContentTab({ onChanged }: { onChanged: () => void }) {
  const { data: types } = useData<CType[]>("/api/admin/content-types");
  const { data: users } = useData<AdminUser[]>("/api/admin/users");
  const { data: markets } = useData<Market[]>("/api/markets");
  const { data: principals } = useData<Principal[]>("/api/principals");
  const [typeKey, setTypeKey] = useState("deals");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const n = useNotice();

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const type = types?.find(t => t.key === typeKey);
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (archived && type?.archive) params.set("archived", "1");
  const { data: rows, refetch } = useData<Row[]>(type ? `/api/admin/content/${typeKey}?${params}` : null, [typeKey]);

  const options = (f: CField, current: unknown): { value: string; label: string }[] => {
    let opts: { value: string; label: string }[] = [];
    if (f.type === "select") opts = (f.options || []).map(o => ({ value: o, label: o }));
    if (f.type === "partner") opts = (users || []).map(u => ({ value: u.id, label: u.name }));
    if (f.type === "market") opts = (markets || []).map(m => ({ value: m.name, label: m.name }));
    if (f.type === "principal") opts = (principals || []).map(p => ({ value: p.id, label: p.name }));
    const cur = current == null ? "" : String(current);
    if (cur && !opts.some(o => o.value === cur)) opts = [{ value: cur, label: cur }, ...opts];
    return opts;
  };

  const open = (r: Row) => {
    setEditing(r.id);
    const f: Record<string, unknown> = {};
    for (const fl of type!.fields) {
      const v = r[fl.key];
      f[fl.key] = fl.type === "date" && v ? String(v).slice(0, 10) : v ?? (fl.type === "bool" ? false : "");
    }
    setForm(f);
  };
  const save = async () => {
    try {
      await api.put(`/api/admin/content/${typeKey}/${editing}`, form);
      n.ok("Saved"); setEditing(null); refetch(); onChanged();
    } catch (e) { n.err(e); }
  };
  const remove = async (r: Row) => {
    const title = String(r[type!.titleField] ?? r.id);
    const msg = type!.archive
      ? `Delete "${title}"? It moves to Archived, where you can restore it.`
      : `Permanently delete "${title}"? This can't be undone.`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/api/admin/content/${typeKey}/${r.id}`);
      n.ok(type!.archive ? `"${title}" archived` : `"${title}" deleted`); refetch(); onChanged();
    } catch (e) { n.err(e); }
  };
  const restore = async (r: Row) => {
    try { await api.post(`/api/admin/content/${typeKey}/${r.id}/restore`, {}); n.ok("Restored"); refetch(); onChanged(); }
    catch (e) { n.err(e); }
  };

  const input = (f: CField) => {
    const v = form[f.key];
    const set = (val: unknown) => setForm({ ...form, [f.key]: val });
    if (f.type === "textarea") return <textarea rows={4} value={String(v ?? "")} onChange={e => set(e.target.value)} />;
    if (f.type === "bool") return <input type="checkbox" className="set-bool" checked={!!v} onChange={e => set(e.target.checked)} />;
    if (["select", "partner", "market", "principal"].includes(f.type)) {
      return (
        <select value={String(v ?? "")} onChange={e => set(e.target.value)}>
          {f.type !== "select" && <option value="">—</option>}
          {options(f, v).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step="any"
      value={String(v ?? "")} onChange={e => set(e.target.value)} />;
  };

  return (
    <div className="card set-card">
      <div className="set-card-head">
        <div className="seg">
          {(types || []).map(t => (
            <button key={t.key} className={clsx("seg-btn", t.key === typeKey && "active")}
              onClick={() => { setTypeKey(t.key); setEditing(null); setArchived(false); }}>{t.label}</button>
          ))}
        </div>
        <div className="set-row-actions">
          <input className="set-search" value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${type?.label.toLowerCase() || ""}…`} />
          {type?.archive && (
            <label className="set-check"><input type="checkbox" checked={archived} onChange={e => { setArchived(e.target.checked); setEditing(null); }} /> Archived</label>
          )}
        </div>
      </div>
      {n.el}
      <div className="set-rows">
        {(rows || []).map(r => (
          <div key={r.id}>
            <div className="set-row">
              <div className="set-row-main">
                <div className="set-row-title">{String(r[type?.titleField || "id"] ?? r.id)}</div>
                {r._subtitle && <div className="set-row-sub">{r._subtitle}</div>}
              </div>
              {archived
                ? <div className="set-row-actions"><button className="btn ghost sm" onClick={() => restore(r)}>Restore</button></div>
                : <RowActions onEdit={() => open(r)} onDelete={() => remove(r)} />}
            </div>
            {editing === r.id && type && (
              <div className="set-form">
                <div className="set-grid">
                  {type.fields.map(f => (
                    <Field key={f.key} label={f.label} wide={f.type === "textarea"}>{input(f)}</Field>
                  ))}
                </div>
                <div className="set-form-foot">
                  <button className="btn ghost sm" onClick={() => setEditing(null)}>Cancel</button>
                  <button className="btn primary sm" onClick={save}>Save</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows && rows.length === 0 && <div className="set-empty">{archived ? "Nothing archived." : "Nothing found."}</div>}
        {rows && rows.length === 500 && <div className="set-empty">Showing the first 500 — search to narrow down.</div>}
      </div>
    </div>
  );
}
